import React, { useEffect, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import styles from './TokenSettingsTab.module.css';
import { settingsApi } from '../../../lib/apiClient';
import { toast } from '../../../lib/toast';
import { FieldMeta, SettingsMap } from '../../../types/webchatTabsTypes';
import { hasValue } from '../settingsUtils';
import { SECTION_META, FIELDS_BY_SECTION, ALL_FIELDS } from './tokenFieldsConfig';
import { normalizeResponse, matchesSearch } from './tokenSettingsUtils';
import { FieldRenderProps } from './types';
import NumberCard from './NumberCard';
import ToggleRow from './ToggleRow';
import TextCard from './TextCard';

const TokenSettingsTab: React.FC = () => {
  const [settings, setSettings] = useState<SettingsMap | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [initialDraft, setInitialDraft] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState(SECTION_META[0].id);
  const [showKeys, setShowKeys] = useState(false);
  const [search, setSearch] = useState('');
  const [advancedOpen, setAdvancedOpen] = useState<Record<string, boolean>>({});

  const loadSettings = async (showErrorToast = true) => {
    try {
      const raw = await settingsApi.getWebchatSettings();
      const fromServer = normalizeResponse(raw);
      const normalized: SettingsMap = { ...fromServer };
      const initial: Record<string, string> = {};

      for (const { key, fallbackDefault } of ALL_FIELDS) {
        const rawValue = fromServer[key]?.value;
        const rawDefault = fromServer[key]?.default;
        const def = hasValue(rawDefault) ? (rawDefault as string | number) : fallbackDefault;
        const value = hasValue(rawValue) ? (rawValue as string | number) : def;
        normalized[key] = { value, default: def };
        initial[key] = String(value);
      }

      setSettings(normalized);
      setDraft(initial);
      setInitialDraft(initial);
    } catch {
      if (showErrorToast) toast.error('Failed to load Token settings');
    }
  };

  useEffect(() => {
    loadSettings().finally(() => setLoading(false));
  }, []);

  const dirtyKeys = useMemo(
    () => ALL_FIELDS.filter(({ key }) => draft[key] !== (initialDraft[key] ?? '')).map(f => f.key),
    [draft, initialDraft],
  );
  const isDirty = dirtyKeys.length > 0;

  const matchedKeys = useMemo(
    () => (search.trim() ? ALL_FIELDS.filter(f => matchesSearch(f, search.trim())).map(f => f.key) : []),
    [search],
  );

  useEffect(() => {
    if (!search.trim()) return;
    const activeHasMatch = FIELDS_BY_SECTION[activeSection]?.some(f => matchedKeys.includes(f.key));
    if (activeHasMatch) return;
    const nextSection = SECTION_META.find(s => FIELDS_BY_SECTION[s.id].some(f => matchedKeys.includes(f.key)));
    if (nextSection) setActiveSection(nextSection.id);
  }, [search, matchedKeys, activeSection]);

  useEffect(() => {
    if (!search.trim()) return;
    setAdvancedOpen(prev => {
      const next = { ...prev };
      for (const section of SECTION_META) {
        const hasAdvancedMatch = FIELDS_BY_SECTION[section.id].some(f => f.advanced && matchedKeys.includes(f.key));
        if (hasAdvancedMatch) next[section.id] = true;
      }
      return next;
    });
  }, [search, matchedKeys]);

  const handleNumberChange = (key: string, value: string) => setDraft(d => ({ ...d, [key]: value }));
  const handleToggle = (key: string) => setDraft(d => ({ ...d, [key]: d[key] === 'true' ? 'false' : 'true' }));

  const persistUpdates = async (updates: Record<string, string>, successMessage: string, failureMessage: string) => {
    if (Object.keys(updates).length === 0) {
      toast.success('No changes to save');
      return;
    }
    setSaving(true);
    try {
      await Promise.all(Object.entries(updates).map(([key, value]) => settingsApi.setValue(key, { value })));
      await loadSettings(false);
      toast.success(successMessage);
    } catch {
      toast.error(failureMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAll = async () => {
    for (const { key, label, min, type } of ALL_FIELDS) {
      if (type !== 'number') continue;
      const num = Number(draft[key]?.trim());
      if (isNaN(num)) { toast.error(`${label}: please enter a valid number`); return; }
      if (min !== undefined && num < min) { toast.error(`${label}: must be >= ${min}`); return; }
    }
    const updates: Record<string, string> = {};
    for (const { key, type } of ALL_FIELDS) {
      const raw = draft[key]?.trim() ?? '';
      if (raw === (initialDraft[key] ?? '')) continue;
      updates[key] = type === 'number' ? String(Number(raw)) : raw;
    }
    await persistUpdates(updates, 'Token settings saved', 'Failed to save Token settings');
  };

  const handleResetAll = async () => {
    if (!settings) return;
    const updates = Object.fromEntries(
      ALL_FIELDS.map(({ key, fallbackDefault }) => [key, String(settings[key]?.default ?? fallbackDefault)])
    );
    await persistUpdates(updates, 'Reset all to defaults', 'Failed to reset settings');
  };

  const handleResetSection = async () => {
    if (!settings) return;
    const fields = FIELDS_BY_SECTION[activeSection] ?? [];
    const updates = Object.fromEntries(
      fields.map(({ key, fallbackDefault }) => [key, String(settings[key]?.default ?? fallbackDefault)])
    );
    await persistUpdates(updates, 'Section reset to defaults', 'Failed to reset section');
  };

  if (loading) return <div className={styles.loading}>Loading…</div>;
  if (!settings) return null;

  const renderProps = (field: FieldMeta): FieldRenderProps => ({
    field,
    draft,
    initialDraft,
    settings,
    showKeys,
    highlighted: matchedKeys.includes(field.key),
    saving,
    onNumberChange: handleNumberChange,
    onTextChange: handleNumberChange,
    onToggle: handleToggle,
  });

  const renderContextHistory = () => {
    const fields = FIELDS_BY_SECTION['context-history'];
    const byKey = Object.fromEntries(fields.map(f => [f.key, f]));
    return (
      <>
        <div className={styles.group}>
          <p className={styles.groupLabel}>Conversation limits</p>
          <div className={styles.cardGrid}>
            <NumberCard {...renderProps(byKey['webchat_max_history_messages'])} />
            <NumberCard {...renderProps(byKey['webchat_max_history_tokens'])} />
          </div>
        </div>
        {/* <div className={styles.group}>
          <p className={styles.groupLabel}>Pinned context</p>
          <div className={styles.cardGrid} style={{ gridTemplateColumns: '1fr' }}>
            <ToggleRow {...renderProps(byKey['webchat_enable_pinned_facts'])} />
          </div>
          <div className={styles.cardGrid} style={{ marginTop: '0.875rem' }}>
            <NumberCard {...renderProps(byKey['webchat_max_pinned_fact_tokens'])} />
          </div>
        </div> */}
      </>
    );
  };

  const renderSummarization = () => {
    const fields = FIELDS_BY_SECTION['summarization'];
    const byKey = Object.fromEntries(fields.map(f => [f.key, f]));
    const enabled = draft['webchat_enable_history_summarization'] === 'true';
    const trigger = Number(draft['webchat_history_summary_trigger_tokens']) || 0;
    const rawTail = Number(draft['webchat_history_keep_raw_tail_tokens']) || 0;
    const target = Number(draft['webchat_history_summary_target_tokens']) || 0;
    const recentPct = trigger > 0 ? Math.max(0, Math.min(100, (rawTail / trigger) * 100)) : 0;
    const oldPct = 100 - recentPct;
    const advancedFields = fields.filter(f => f.advanced);
    const isOpen = advancedOpen['summarization'] ?? false;

    return (
      <>
        <div className={styles.group}>
          <ToggleRow {...renderProps(byKey['webchat_enable_history_summarization'])} big />
        </div>

        <div className={styles.group}>
          <p className={styles.groupLabel}>When should summarization happen?</p>
          <div className={styles.cardGrid}>
            <NumberCard {...renderProps(byKey['webchat_history_summary_trigger_tokens'])} />
            <NumberCard {...renderProps(byKey['webchat_history_keep_raw_tail_tokens'])} />
          </div>

          <div className={`${styles.linkedCard} ${!enabled ? styles.cardDisabled : ''}`} style={{ marginTop: '0.875rem' }}>
            <p className={styles.linkedTitle}>Summarization flow</p>
            <div className={styles.flowTrack}>
              <div className={styles.flowSegmentOld} style={{ width: `${oldPct}%` }} />
              <div className={styles.flowSegmentRecent} style={{ width: `${recentPct}%` }} />
            </div>
            <div className={styles.flowLegend}>
              <span>Older → summarized</span>
              <span>{rawTail.toLocaleString()} kept verbatim</span>
            </div>
            <div className={styles.flowArrowRow}>compressed to</div>
            <div className={styles.flowTargetChip}><span>~{target.toLocaleString()} token summary</span></div>
          </div>
        </div>

        <div className={styles.group}>
          <p className={styles.groupLabel}>Summary size</p>
          <div className={styles.cardGrid} style={{ gridTemplateColumns: '1fr' }}>
            <NumberCard {...renderProps(byKey['webchat_history_summary_target_tokens'])} />
          </div>
        </div>

        <details className={styles.advanced} open={isOpen} onToggle={e => setAdvancedOpen(a => ({ ...a, summarization: (e.target as HTMLDetailsElement).open }))}>
          <summary className={styles.advancedSummary}>▸ Advanced settings</summary>
          <div className={styles.advancedBody}>
            {advancedFields.map(f => f.type === 'boolean'
              ? <ToggleRow key={f.key} {...renderProps(f)} />
              : <div key={f.key} className={styles.cardGrid} style={{ gridTemplateColumns: '1fr' }}><NumberCard {...renderProps(f)} /></div>
            )}
          </div>
        </details>
      </>
    );
  };

  const renderBasicSection = (id: string) => {
    const fields = FIELDS_BY_SECTION[id] ?? [];
    const toggles = fields.filter(f => f.type === 'boolean');
    const numbers = fields.filter(f => f.type === 'number');
    const texts = fields.filter(f => f.type === 'string');
    return (
      <>
        {toggles.length > 0 && (
          <div className={styles.group}>
            {toggles.map(f => <div key={f.key} style={{ marginBottom: '0.625rem' }}><ToggleRow {...renderProps(f)} /></div>)}
          </div>
        )}
        {texts.length > 0 && (
          <div className={styles.group}>
            <div className={styles.cardGrid} style={{ gridTemplateColumns: '1fr' }}>
              {texts.map(f => <TextCard key={f.key} {...renderProps(f)} />)}
            </div>
          </div>
        )}
        {numbers.length > 0 && (
          <div className={styles.group}>
            <div className={styles.cardGrid}>
              {numbers.map(f => <NumberCard key={f.key} {...renderProps(f)} />)}
            </div>
          </div>
        )}
      </>
    );
  };

  const activeMeta = SECTION_META.find(s => s.id === activeSection)!;

  return (
    <div className={styles.page}>
      <div className={styles.headerRow}>
        <div>
          <h2>Token Settings</h2>
          <p>Configure context, tools, budgets and execution for webchat conversations.</p>
        </div>
        <div className={styles.headerActions}>
          <button type="button" className={styles.showKeysBtn} onClick={() => setShowKeys(s => !s)}>
            {showKeys ? 'Hide setting keys' : 'Show setting keys'}
          </button>
          <div className={styles.searchWrap}>
            <Search size={14} className={styles.searchIcon} />
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Search settings"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className={styles.body}>
        <div className={styles.nav}>
          {SECTION_META.map(s => (
            <button
              key={s.id}
              className={`${styles.navItem} ${activeSection === s.id ? styles.navItemActive : ''}`}
              onClick={() => setActiveSection(s.id)}
            >
              {s.heading}
            </button>
          ))}
        </div>

        <div className={styles.content}>
          <div className={styles.contentHeader}>
            <h3>{activeMeta.heading}</h3>
            <p>{activeMeta.subtitle}</p>
          </div>

          {activeSection === 'context-history' && renderContextHistory()}
          {activeSection === 'summarization' && renderSummarization()}
          {(activeSection === 'run-budgets' || activeSection === 'results-data' || activeSection === 'reliability' || activeSection === 'data-analyst') && renderBasicSection(activeSection)}
        </div>
      </div>

      <div className={styles.footer}>
        <span className={styles.footerStatus}>{isDirty ? `${dirtyKeys.length} unsaved change${dirtyKeys.length === 1 ? '' : 's'}` : 'No unsaved changes'}</span>
        <div className={styles.footerActions}>
          <button type="button" className={styles.resetBtn} disabled={saving} onClick={handleResetSection}>Reset section</button>
          <button type="button" className={styles.resetBtn} disabled={saving} onClick={handleResetAll}>Reset all to defaults</button>
          <button type="button" className={styles.saveBtn} disabled={!isDirty || saving} onClick={handleSaveAll}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TokenSettingsTab;
